import {
  deleteMagazineByIdAPI,
  getMagazineByIdAPI,
} from '@/src/apis/rest/magazine';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import DetailSection from '@/src/components/organism/DetailSection';
import { NAV_CONST } from '@/src/constants/nav';
import dayjs from 'dayjs';
import { useState } from 'react';
import MagazineViewHistoriesModal from '@/src/pages/Magazine/components/MagazineViewHistoriesModal';
import MagazinePreview from '@/src/pages/Magazine/components/MagazinePreview';
import { webviewDataInjection } from '@/src/helpers/webviewHelper';
import { UNIT_STRINGS } from '@/src/constants/unit';
import {
  MagazineWebviewMapper,
  MagazineWebviewTemplate,
} from '@/src/helpers/webviewHelper/magazine';
import { getContentTemplatesAPI } from '@/src/apis/rest/contentTemplate';
import ContentsPreview from '@/src/components/molecule/ContentsPreview';
import { OPTIONS_CONST } from '@/src/constants/options';
import SectionCard from '@/src/components/molecule/SectionCard';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import { APIError } from '@/src/models/common/apiError';
import AlertDialog from '@/src/components/atom/AlertDialog';
import Typo from '@/src/components/atom/Typo';
import { inAppLinkRegex } from '@/src/utils/regex';

type ModalState = 'NONE' | 'HISTORIES' | 'DELETE' | 'BANNER_CONNECT_ERROR';

const LIST_PATH = PATH.MAGAZINE_LIST;
const BACK_PATH = PATH.MAGAZINE_LIST;
const EDIT_PATH = PATH.MAGAZINE_EDIT;

export default function MagazineDetailPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();

  // state
  const [modalState, setModalState] = useState<ModalState>('NONE');

  // query
  const { data: magazineData } = useQuery({
    queryKey: ['magazine', id],
    queryFn: () => getMagazineByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });
  const { data: contentTemplateData } = useQuery({
    queryKey: ['content-template', 'MAGAZINE'],
    queryFn: () => {
      return getContentTemplatesAPI({ type: 'MAGAZINE' });
    },
  });

  const deleteMagazineMutation = useMutation({
    mutationFn: deleteMagazineByIdAPI,
  });

  const contentsMarkup = webviewDataInjection<MagazineWebviewTemplate>(
    contentTemplateData?.rows?.[0]?.markup || '',
    MagazineWebviewMapper(magazineData),
  );
  const hasOptionalInfo =
    !!magazineData?.bottomButtonDisplayYn ||
    !!magazineData?.sponsorImage ||
    !!magazineData?.sponsorDescription ||
    !!magazineData?.sponsorPageLink ||
    false;

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };
  const handleDeleteMagazine = () => {
    deleteMagazineMutation.mutate(
      { id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.deleteSuccess.title,
            description: TOAST_CONST.deleteSuccess.description,
          });
          setTimeout(() => {
            navigate(LIST_PATH, { replace: true });
          }, 0);
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'THIS_CONTENT_HAVE_BANNER') {
            handleOpenModal('BANNER_CONNECT_ERROR');
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>

          <div className="flex items-center gap-2">
            <Button
              className="w-40"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => handleOpenModal('DELETE')}
              isLoading={deleteMagazineMutation.isPending}
            >
              {BUTTON_CONST.delete}
            </Button>

            <Link to={EDIT_PATH.replace(':id', id || '')}>
              <Button className="w-40" buttonSize="medium">
                {BUTTON_CONST.editShort}
              </Button>
            </Link>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.magazineManagement,
          NAV_CONST.magazineDetail,
        ]}
        pageTitle={NAV_CONST.magazineDetail}
      />
      {magazineData && (
        <div className="mb-[100px] flex flex-col gap-4">
          <DetailSection
            sectionTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  매거진 기본 정보
                </span>
              </div>
            }
            data={magazineData}
            contents={[
              [
                {
                  key: 'magazineType',
                  lable: '매거진 유형',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.notAdvertisement,
                        key: 'common',
                        badgeColor: 'lightYellow',
                      },
                      {
                        label: OPTIONS_CONST.advertisement,
                        key: 'advertising',
                        badgeColor: 'lightPurple',
                      },
                    ],
                  }),
                },

                {
                  key: 'postedAt',
                  lable: '게시 기간',
                  valueSetter: data =>
                    `${dayjs(data?.magazinePostStartedAt).format(
                      'YYYY-MM-DD',
                    )} ~ ${dayjs(data?.magazinePostEndedAt).format('YYYY-MM-DD')}`,
                },
                {
                  key: 'isExposed',
                  lable: '노출 여부',
                  valueSetter: data => (data?.isExposed ? 'true' : 'false'),
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.exposed,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notExposed,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: 'priorityDisplayYn',
                  lable: '우선 노출 여부',
                  valueSetter: data =>
                    data?.priorityDisplayYn ? 'true' : 'false',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.applied,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notApplied,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: 'thumbnailImage',
                  lable: '썸네일 이미지',
                  valueSetter: data =>
                    data?.thumbnailImage?.bufferFullUrl || '',
                  render: DetailSctionRenderer.IMAGE({
                    width: 130,
                    height: 195,
                  }),
                },
              ],
              [
                {
                  key: 'awardedCreditYn',
                  lable: '크레딧 지급 여부',
                  valueSetter: data =>
                    data?.awardedCreditYn ? 'true' : 'false',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.awarded,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notAwarded,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: ['creditTemplate', 'title'],
                  lable: '지급 크레딧',
                  valueSetter: data =>
                    data?.creditTemplate
                      ? `[매거진] ${
                          data?.creditTemplate?.title
                        } (${data?.creditTemplate?.credit.toLocaleString()}${
                          UNIT_STRINGS.CREDIT
                        })`
                      : '-',
                },
                {
                  key: 'magazineViewHistoryCount',
                  lable: '조회 수',
                  valueSetter: data => {
                    return DetailSctionRenderer.NUMBER()(
                      typeof data?.magazineViewHistoryCount === 'number'
                        ? String(data?.magazineViewHistoryCount)
                        : '0',
                    );
                  },
                  render: e => (
                    <div className="flex items-center gap-2">
                      <p className="text-b16r text-gray6">
                        {DetailSctionRenderer.PREFIX_SUFFIX({ suffix: '회' })(
                          e,
                        )}
                      </p>
                      {!isNaN(Number(e)) && Number(e) > 0 && (
                        <Button
                          buttonColor="secondary"
                          buttonSize="xSmall"
                          onClick={() => handleOpenModal('HISTORIES')}
                        >
                          조회한 회원 목록 보기
                        </Button>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'magazineBookmarkCount',
                  lable: '북마크 수',
                  valueSetter: data => {
                    return DetailSctionRenderer.NUMBER()(
                      typeof data?.magazineBookmarkCount === 'number'
                        ? String(data?.magazineBookmarkCount)
                        : '0',
                    );
                  },
                  render: DetailSctionRenderer.PREFIX_SUFFIX({ suffix: '건' }),
                },
                {
                  key: 'createdAt',
                  lable: '등록 일시',
                  render: DetailSctionRenderer.DATE({
                    format: 'YYYY-MM-DD HH:mm:ss',
                  }),
                },
                {
                  key: ['administrator', 'name'],
                  lable: '등록 관리자',
                },
              ],
            ]}
          />
          <DetailSection
            isCollapsible
            defaultOpen={hasOptionalInfo}
            sectionTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  매거진 선택 정보
                </span>
              </div>
            }
            data={magazineData}
            contents={[
              [
                {
                  key: 'sponsorImage',
                  lable: '제휴사 이미지',
                  valueSetter: data => data?.sponsorImage?.bufferFullUrl || '',
                  render: DetailSctionRenderer.IMAGE({
                    width: 128,
                    height: 128,
                  }),
                },
                {
                  key: 'sponsorDescription',
                  lable: '제휴사 설명',
                },
                {
                  key: 'sponsorPageLink',
                  lable: '제휴사 홈페이지 링크',
                  valueSetter: data => {
                    return data?.sponsorPageLink || '';
                  },
                  render: (data: string) =>
                    data ? (
                      <Link
                        to={data}
                        target="_blank"
                        className="break-all text-b16r text-gray6 underline"
                      >
                        {data}
                      </Link>
                    ) : (
                      '-'
                    ),
                },
              ],
              [
                {
                  key: 'bottomButtonDisplayYn',
                  lable: '하단 버튼 노출 여부',
                  valueSetter: data =>
                    data?.bottomButtonDisplayYn ? 'true' : 'false',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.displayed,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notDisplayed,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: 'bottomButtonName',
                  lable: '하단 버튼 명',
                },
                {
                  key: 'bottomButtonLink',
                  lable: '하단 버튼 링크',
                  valueSetter: data => {
                    return data?.bottomButtonLink || '';
                  },
                  render: (data: string) =>
                    data ? (
                      inAppLinkRegex.test(data) ? (
                        <Typo typoType="b16r" typoColor="gray6">
                          {data}
                        </Typo>
                      ) : (
                        <Link
                          to={data}
                          target="_blank"
                          className="break-all text-b16r text-gray6 underline"
                        >
                          {data}
                        </Link>
                      )
                    ) : (
                      '-'
                    ),
                },
              ],
            ]}
          />
          <SectionCard
            cardTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  매거진 내용
                </span>
              </div>
            }
          >
            <div className="flex gap-6">
              <ContentsPreview
                title={magazineData?.title}
                subTitle={magazineData?.subTitle}
                contents={magazineData?.contents}
              />
              <MagazinePreview contentsMarkup={contentsMarkup} />
            </div>
          </SectionCard>
        </div>
      )}
      {renderBottomFixedContent()}

      {/* modal */}
      {modalState === 'HISTORIES' && (
        <MagazineViewHistoriesModal
          open={modalState === 'HISTORIES'}
          onOpenChange={() => handleToggleModal('HISTORIES')}
          id={Number(id)}
        />
      )}

      {modalState === 'DELETE' && (
        <AlertDialog
          open={modalState === 'DELETE'}
          onOpenChange={() => handleToggleModal('DELETE')}
          okText={BUTTON_CONST.ok}
          onOk={handleDeleteMagazine}
          title="매거진 삭제"
          okButtonProps={{
            isLoading: deleteMagazineMutation.isPending,
          }}
        >
          <p className="whitespace-pre-wrap">
            매거진을 삭제하시겠습니까?
            <br />
            삭제된 매거진은 사용자에게 노출되지 않습니다.
          </p>
        </AlertDialog>
      )}

      {modalState === 'BANNER_CONNECT_ERROR' && (
        <AlertDialog
          open={modalState === 'BANNER_CONNECT_ERROR'}
          onOpenChange={() => handleToggleModal('BANNER_CONNECT_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="삭제 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            연결된 배너가 있어서 삭제가 불가능합니다.
            <br />
            배너를 확인해주세요.
          </p>
        </AlertDialog>
      )}
    </>
  );
}
